import React, { useEffect, useRef, useState } from 'react';
import { Button, Table, Spinner } from 'react-bootstrap';

const PaginationTable = ({ data = [], itemsPerPage = 10, columns, renderItem, currentPage, onPageChange, totalPages }) => {
    // Loader al cambiar de página. Solo con paginación en el servidor
    // (totalPages definido): en ese caso la página nueva llega por la red y
    // mientras tanto se oscurece la tabla. Se apaga cuando cambia `data`, con
    // un tope de 30 s por si la respuesta falla y los datos no cambian.
    const [esperando, setEsperando] = useState(false);
    const tope = useRef(null);
    useEffect(() => {
        setEsperando(false);
        clearTimeout(tope.current);
    }, [data]);
    useEffect(() => () => clearTimeout(tope.current), []);

    const cambiarPagina = (numero) => {
        if (numero === currentPage) return;
        if (totalPages) {
            setEsperando(true);
            clearTimeout(tope.current);
            tope.current = setTimeout(() => setEsperando(false), 30000);
        }
        onPageChange(numero);
    };

    // Asegura que data sea un array
    if (!Array.isArray(data)) {
        console.warn('Expected data to be an array but received:', data);
        data = []; // Falla seguro: establece data como un array vacío
    }

    // Si `totalPages` no está definido, calculamos el número total de páginas en función de los datos locales
    const calculatedTotalPages = totalPages || Math.ceil(data.length / itemsPerPage);

    // Si `totalPages` no está definido, calculamos `currentItems` aquí
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = totalPages ? data : data.slice(indexOfFirstItem, indexOfLastItem);

    // Configuración de la paginación visible
    const visiblePageRange = 3;
    const startPage = Math.max(1, currentPage - Math.floor(visiblePageRange / 2));
    const endPage = Math.min(calculatedTotalPages, startPage + visiblePageRange - 1);

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
    }

    const handlePreviousPage = () => {
        if (currentPage > 1) cambiarPagina(currentPage - 1);
    };

    const handleNextPage = () => {
        if (currentPage < calculatedTotalPages) cambiarPagina(currentPage + 1);
    };

    return (
        <div style={{ position: 'relative' }}>
            {esperando && (
                <div
                    className="d-flex justify-content-center align-items-start"
                    style={{ position: 'absolute', inset: 0, zIndex: 2, paddingTop: '80px', background: 'rgba(255,255,255,0.6)' }}
                    aria-live="polite"
                >
                    <Spinner animation="border" role="status" />
                    <span className="ms-2 mt-1 text-muted">Cargando página…</span>
                </div>
            )}
            <Table striped bordered hover className="rounded-table">
                <thead>
                    <tr>
                        {columns.map((col, index) => (
                            <th key={index}>{col}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {(totalPages ? data : currentItems).map(renderItem)} {/* Renderiza `data` directamente si paginación en servidor */}
                </tbody>
            </Table>

            <nav className="d-flex justify-content-center">
                <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <Button
                            className="page-link"
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                        >
                            Anterior
                        </Button>
                    </li>
                    {pageNumbers.map(number => (
                        <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                            <Button
                                onClick={() => cambiarPagina(number)}
                                className="page-link"
                                disabled={esperando}
                            >
                                {number}
                            </Button>
                        </li>
                    ))}
                    <li className={`page-item ${currentPage === calculatedTotalPages ? 'disabled' : ''}`}>
                        <Button
                            className="page-link"
                            onClick={handleNextPage}
                            disabled={currentPage === calculatedTotalPages}
                        >
                            Siguiente
                        </Button>
                    </li>
                </ul>
            </nav>
        </div>
    );
};

export default PaginationTable;
